/*
  Cart drawer v2 behaviour (snippets/cart-drawer-v2.liquid).
  - <cart-bundle-items>: lists a bundle line's components from its
    bundle.composition_v2 JSON, fetching each variant from /variants/{id}.js.
    The drawer re-renders by swapping .drawer__inner HTML, so the element
    re-fetches (from cache) every time it connects.
  - A thumbnail strip, plus a closed-by-default Show / Hide list; the choice survives re-renders.
  - Closes header-v2's mega panel / mobile menu when the cart opens.
*/
(() => {
  if (window.__cd2Bound) return;
  window.__cd2Bound = true;

  const variantCache = {};
  const expanded = new Set(); // cart line keys whose item list is open (closed by default)

  function fetchVariant(id) {
    if (!variantCache[id]) {
      variantCache[id] = fetch(`/variants/${id}.js`, { headers: { Accept: 'application/json' } }).then((res) => {
        if (!res.ok) throw new Error(`Variant ${id} ${res.status}`);
        return res.json();
      });
      // Don't cache failures, so the next render can retry.
      variantCache[id].catch(() => delete variantCache[id]);
    }
    return variantCache[id];
  }

  // Variants without their own image fall back to the product's featured image.
  // /variants/{id} redirects to /products/{handle}; a HEAD request gives the handle cheaply.
  const productImageCache = {};
  function fallbackImage(id) {
    if (!productImageCache[id]) {
      productImageCache[id] = fetch(`/variants/${id}`, { method: 'HEAD' })
        .then((res) => {
          const match = new URL(res.url).pathname.match(/\/products\/([^/?#]+)/);
          if (!match) throw new Error('No product handle');
          return fetch(`/products/${match[1]}.js`, { headers: { Accept: 'application/json' } });
        })
        .then((res) => (res.ok ? res.json() : null))
        .then((product) => (product && product.featured_image) || '')
        .catch(() => '');
    }
    return productImageCache[id];
  }

  const imageFor = (variant) =>
    variant.featured_image && variant.featured_image.src ? Promise.resolve(variant.featured_image.src) : fallbackImage(variant.id);

  const numericId = (gid) => (String(gid).match(/(\d+)$/) || [])[1] || null;

  const escapeHtml = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const thumbUrl = (src) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=96` : '');

  // /variants/{id}.js has no separate product title: name is "Product - Variant".
  function productTitle(variant) {
    const suffix = variant.public_title ? ` - ${variant.public_title}` : '';
    return suffix && variant.name.endsWith(suffix) ? variant.name.slice(0, -suffix.length) : variant.name;
  }

  class CartBundleItems extends HTMLElement {
    connectedCallback() {
      let parts = [];
      try {
        parts = JSON.parse(this.dataset.composition || '[]');
      } catch (e) {
        parts = [];
      }
      const lineQty = parseInt(this.dataset.quantity, 10) || 1;
      this.applyExpanded();

      const rows = parts
        .map((part) => ({ id: numericId(part.id), qty: (parseInt(part.quantity, 10) || 1) * lineQty }))
        .filter((part) => part.id);
      if (!rows.length) return this.fail();

      Promise.all(rows.map((row) => fetchVariant(row.id)))
        .then((variants) => Promise.all(variants.map(imageFor)).then((images) => ({ variants, images })))
        .then(({ variants, images }) => {
          if (!this.isConnected) return;
          const items = variants.map((variant, i) => ({
            qty: rows[i].qty,
            title: productTitle(variant),
            // "Sheet Set / White / Queen" -> "Sheet Set, White, Queen"
            option: (variant.public_title || '').split(' / ').join(', '),
            image: images[i],
          }));
          const img = (item, size) =>
            item.image ? `<img src="${escapeHtml(thumbUrl(item.image))}" alt="" width="${size}" height="${size}" loading="lazy">` : '';

          // Strip: one thumbnail per item, a count badge when there is more than one.
          this.querySelector('.cd2-strip').innerHTML = items
            .map(
              (item) => `<li class="cd2-strip__item" title="${escapeHtml(item.title)}">
                <span class="cd2-strip__img">${img(item, 34)}</span>${
                  item.qty > 1 ? `<span class="cd2-strip__count">${item.qty}</span>` : ''
                }<span class="visually-hidden">${item.qty} × ${escapeHtml(item.title)}</span>
              </li>`
            )
            .join('');

          // List (when opened): one line per item, variant in grey; quantity as a
          // badge on the thumbnail (same as the strip), not "2×" in the text.
          this.querySelector('.cd2-bundle__list').innerHTML = items
            .map(
              (item) => `<li class="cd2-bundle__item">
                <span class="cd2-bundle__thumb">
                  <span class="cd2-bundle__img">${img(item, 28)}</span>${
                    item.qty > 1 ? `<span class="cd2-bundle__count" aria-hidden="true">${item.qty}</span>` : ''
                  }
                </span>
                <span class="cd2-bundle__name"><span class="visually-hidden">${item.qty} × </span>${escapeHtml(item.title)}${
                  item.option ? ` <span class="cd2-bundle__option">· ${escapeHtml(item.option)}</span>` : ''
                }</span>
              </li>`
            )
            .join('');
        })
        .catch(() => this.fail());
    }

    applyExpanded() {
      const open = expanded.has(this.dataset.key);
      const list = this.querySelector('.cd2-bundle__list');
      const toggle = this.querySelector('[data-cd2-bundle-toggle]');
      if (list) list.hidden = !open;
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', String(open));
      const label = toggle.querySelector('[data-cd2-bundle-label]');
      if (label) label.textContent = open ? toggle.dataset.labelHide : toggle.dataset.labelShow;
    }

    // A component could not be loaded: keep the "Bundle" label, drop the strip, toggle and list.
    fail() {
      this.hidden = true;
    }
  }

  if (!customElements.get('cart-bundle-items')) customElements.define('cart-bundle-items', CartBundleItems);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cd2-bundle-toggle]');
    const bundle = toggle && toggle.closest('cart-bundle-items');
    if (!bundle) return;
    const key = bundle.dataset.key;
    expanded.has(key) ? expanded.delete(key) : expanded.add(key);
    bundle.applyExpanded();
  });

  /* ---------- Open / close ---------- */

  // Like the legacy drawer, the open cart covers the whole screen, header included, so the
  // navbar can't be hovered until the cart is closed. Opening it closes any open mega panel
  // or mobile menu first.
  function init() {
    const drawer = document.querySelector('cart-drawer.cd2');
    if (!drawer) return;
    const header = document.querySelector('header-v2');

    new MutationObserver(() => {
      const open = drawer.classList.contains('active');
      if (open === drawer.__cd2Open) return;
      drawer.__cd2Open = open;
      if (open && header && typeof header.closeAll === 'function') header.closeAll(false);
      document
        .querySelectorAll('header-v2 .cart-icon-bubble')
        .forEach((link) => link.setAttribute('aria-expanded', String(open)));
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
